import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Dash, Workspace } from "../../../types/Dash";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useActions, { useAppSelector } from "../../../app/hooks";
import { selectLibrary, selectRepoId } from "../../library/librarySlice";
import { Automation } from "../../../types/Automation";
import { AutoLine } from "../../../types/AutoLine";

export default function AutomationList(props: {
  parentCloseHandle: () => void;
  //currentWorkspace: Workspace | null;
  runOrManage: 'run' | 'manage'
}) {
  const actions = useActions();

  const viewRepoId = useAppSelector(selectRepoId);
  
  const [createMode, setCreateMode] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [createAutoLineMode, setCreateAutoLineMode] = React.useState<boolean>(false);
  const [editAutoLineMode, setEditAutoLineMode] = React.useState<boolean>(false);
  const [selectedAutomation, setSelectedAutomation] = React.useState<Automation | null>(null);
  const [selectedAutoLine, setSelectedAutoLine] = React.useState<AutoLine | null>(null);

  const [name, setName] = useState("");
  const [nameError, setNameError] = React.useState("");
  const [providesCode, setProvidesCode] = useState(true);

  const [line, setLine] = useState("");
  const [lineError, setLineError] = React.useState("");
  const [hostId, setHostId] = useState<number | null>(null);

  const librarySlice = useAppSelector(selectLibrary);

  // Grab the current automations and hosts for the workspace
  useEffect(() => {
    actions.getAutomations();
    actions.getHosts();
  }, []);

  // Fetch AutoLines when an automation is selected
  useEffect(() => {
    if (selectedAutomation) {
      actions.getAutoLines(selectedAutomation.id);
    }
  }, [selectedAutomation]);

  function handleNameChange(e: any) {
    if (e.target.value.trim() === "") {
      setNameError("Invalid name");
    } else setNameError("");
    setName(e.target.value);
  }

  function handleSelectAutomation(automation: Automation) {
    setSelectedAutomation(automation);
  }

  function resetFormFields() {
    setName("");
    setNameError("");
    setProvidesCode(true);
    setLine("");
    setLineError("");
    setHostId(null);
    setCreateMode(false);
    setEditMode(false);
    setCreateAutoLineMode(false);
    setEditAutoLineMode(false);
    setSelectedAutoLine(null);
  }

  function handleBackToList() {
    setSelectedAutomation(null);
    setCreateAutoLineMode(false);
    setEditAutoLineMode(false);
    setSelectedAutoLine(null);
    setLine("");
    setLineError("");
    setHostId(null);
  }

  function handleEditAutomation(automation: Automation, e: React.MouseEvent) {
    e.stopPropagation();
    setSelectedAutomation(automation);
    setEditMode(true);
    setName(automation.name || "");
    setProvidesCode(automation.provides_code ?? true);
  }

  function handleUpdateAutomation() {
    if (selectedAutomation) {
      actions.updateAutomation({
        automationId: selectedAutomation.id,
        automationUpdateRequest: {
          name: name,
          provides_code: providesCode,
        },
      });
      resetFormFields();
      setSelectedAutomation(null);
    }
  }

  function handleListChange(e: any) {
    if (e.target.value.trim() === "") {
      setLineError("Invalid line");
    } else setLineError("");
    setLine(e.target.value);
  }

  function handleHostIdChange(e: any) {
    const value = e.target.value;
    setHostId(value === "" || value === -1 ? null : Number(value));
  }

  function handleCreateAutomation() {
    actions.createAutomation({
      name: name,
      repo_id: viewRepoId,
      provides_code: providesCode,
    });
    resetFormFields();
  }

  function handleCreateAutoLine() {
    if (!selectedAutomation) return;

    actions.createAutoLine({
      line: line,
      host_id: hostId,
      automation_id: selectedAutomation.id,
    });
    setCreateAutoLineMode(false);
    setLine("");
    setLineError("");
    setHostId(null);
  }

  function handleEditAutoLine(autoline: AutoLine, e: React.MouseEvent) {
    e.stopPropagation();
    setSelectedAutoLine(autoline);
    setEditAutoLineMode(true);
    setLine(autoline.line);
    setHostId(autoline.host_id);
    setLineError("");
  }

  function handleUpdateAutoLine() {
    if (!selectedAutomation || !selectedAutoLine) return;
    actions.updateAutoLine({
      automationId: selectedAutomation.id,
      autoLineId: selectedAutoLine.id,
      autoLineUpdateRequest: { line, host_id: hostId, automation_id: selectedAutomation.id },
    });
    setEditAutoLineMode(false);
    setSelectedAutoLine(null);
    setLine("");
    setLineError("");
    setHostId(null);
  }


  return (
    <Dialog open={true} onClose={() => props.parentCloseHandle()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {selectedAutomation && !editMode ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={handleBackToList} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography>{selectedAutomation.name}</Typography>
          </Box>
        ) : (
          <Typography>Workspace Automations</Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        { !createMode && !editMode && !selectedAutomation && (
          <Box sx={{ pt: 2 }}>
            { librarySlice.automations.length === 0 && (
              <Typography sx={{ mb: 2, textAlign: "center" }}>No automations in the workspace...</Typography>
            )}

          <List>
              { librarySlice.automations.map(automation => (
                <ListItem 
                  key={'automation_' + automation.id}
                  button
                  onClick={() => handleSelectAutomation(automation)}
                  sx={{ py: 1.5 }}
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      onClick={(e) => handleEditAutomation(automation, e)}
                      size="small"
                      title="Edit Automation"
                    >
                      <EditIcon />
                    </IconButton>
                  }
                > 
                  <ListItemText primary={automation.name} />
                </ListItem>
              ))}
          </List>
          </Box>
        )}
        { !createMode && !editMode && !createAutoLineMode && !editAutoLineMode && selectedAutomation && (
          <Box sx={{ pt: 2 }}>
            { librarySlice.auto_lines.filter(_ => _.automation_id === selectedAutomation.id).length === 0 && (
              <Typography sx={{ mb: 2, textAlign: "center" }}>No lines in this automation...</Typography>
            )}

            <List>
              { librarySlice.auto_lines
                .filter(_ => _.automation_id === selectedAutomation.id)
                .map(autoline => (
                  <ListItem 
                    key={'autoline_' + autoline.id}
                    sx={{ py: 1.5 }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={(e) => handleEditAutoLine(autoline, e)}
                        size="small"
                        title="Edit line"
                      >
                        <EditIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText 
                      primary={autoline.line}
                      secondary={autoline.host_id ? `Host ID: ${autoline.host_id}` : "No host"}
                    />
                  </ListItem>
                ))}
            </List>
          </Box>
        )}
        { !createMode && !editMode && editAutoLineMode && selectedAutoLine && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Edit automation line</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="edit-line"
              label="Line"
              error={lineError !== ""}
              helperText={lineError}
              fullWidth
              variant="standard"
              onChange={handleListChange}
              value={line}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              margin="dense"
              id="edit-host"
              label="Host (optional)"
              fullWidth
              variant="standard"
              value={hostId || -1}
              onChange={handleHostIdChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value={-1}>No host (optional)</MenuItem>
              { librarySlice.hosts.map(host => 
                <MenuItem key={'host_edit_' + host.id} value={host.id}>{ host.name }</MenuItem>
              )}
            </TextField>
          </Box>
        )}
        { !createMode && !editMode && createAutoLineMode && selectedAutomation && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Create a new automation line</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="list"
              label="List"
              error={lineError !== ""}
              helperText={lineError}
              fullWidth
              variant="standard"
              onChange={handleListChange}
              value={line}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              margin="dense"
              id="host"
              label="Host (optional)"
              fullWidth
              variant="standard"
              value={hostId || -1}
              onChange={handleHostIdChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value={-1}>No host (optional)</MenuItem>
              { librarySlice.hosts.map(host => 
                <MenuItem key={'host_' + host.id} value={host.id}>{ host.name }</MenuItem>
              )}
            </TextField>
          </Box>
        )}
        { createMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Create a new automation</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Automation name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              onChange={handleNameChange}
              value={name}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={providesCode}
                  onChange={(e) => setProvidesCode(e.target.checked)}
                  name="providesCode"
                />
              }
              label="Provides code"
            />
          </Box>
        )}
        { editMode && selectedAutomation && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Edit automation</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Automation name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              onChange={handleNameChange}
              value={name}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={providesCode}
                  onChange={(e) => setProvidesCode(e.target.checked)}
                  name="providesCode"
                />
              }
              label="Provides code"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button 
          onClick={() => {
            if (createMode || editMode || createAutoLineMode || editAutoLineMode) {
              resetFormFields();
              if (editMode) {
                setSelectedAutomation(null);
              }
            } else {
              props.parentCloseHandle();
            }
          }}
        >
          Cancel
        </Button>
        { !createMode && !editMode && !createAutoLineMode && !selectedAutomation && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateMode(true)}
          >
            Create Automation
          </Button>
        )}
        { !createMode && !editMode && !createAutoLineMode && !editAutoLineMode && selectedAutomation && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateAutoLineMode(true)}
          >
            Create Line
          </Button>
        )}
        { editAutoLineMode && selectedAutoLine && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateAutoLine}
            disabled={line.trim() === "" || lineError !== ""}
          >
            Update
          </Button>
        )}
        { createMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateAutomation}
            disabled={name.trim() === "" || nameError !== ""}
          >
            Confirm
          </Button>
        )}
        { editMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateAutomation}
            disabled={name.trim() === "" || nameError !== ""}
          >
            Update
          </Button>
        )}
        { createAutoLineMode && (
          <Button
                  variant="contained"
                  color="primary"
            onClick={handleCreateAutoLine}
            disabled={line.trim() === "" || lineError !== ""}
          >
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
